import {
  Check,
  Disc,
  Hourglass,
  Link2,
  MessageSquare,
  Mic,
  MicOff,
  Pin,
  PinOff,
  ScreenShare,
  Search,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserAvatar } from '@/components/user-avatar';
import { cn } from '@/lib/utils';

import type {
  AssignableRole,
  LobbyPendingItem,
  ModerationAction,
  ParticipantRole,
} from '../realtime/frames';
import type { PanelMode, ParticipantTile } from './roomState';
import { signalLevel } from './roomState';
import { SignalBars } from '@/components/signal-bars';
import { ChatPanel } from './ChatPanel';
import type { ChatPanelProps } from './ChatPanel';
import { HandBadge } from './badges';
import { ModerationMenu } from './ModerationMenu';
import { RecentActions } from './RecentActions';
import type { RecentActionsProps } from './RecentActions';
import { RecordingsPanel } from './RecordingsPanel';
import type { RecordingsPanelProps } from './RecordingsPanel';
import { canMuteAll, canModerate, moderationActionsFor } from './moderation';

/** Live presence info of one participant, merged into the tiles from the realtime channel. */
export interface PanelPresence {
  readonly role: ParticipantRole;
  readonly handRaised: boolean;
}

/** Props for the collapsible right-hand side panel. */
export interface SidePanelProps {
  readonly panel: Exclude<PanelMode, never>;
  readonly onPanelChange: (panel: PanelMode) => void;
  readonly onClose: () => void;
  /** Mobile layout: the panel renders as a full-screen overlay sheet instead of a column. */
  readonly isMobile: boolean;
  /** Participant tiles in render order (the local participant first). */
  readonly tiles: readonly ParticipantTile[];
  /** Identities with a live screen share right now. */
  readonly sharingIdentities: ReadonlySet<string>;
  readonly pinnedIdentity: string | null;
  readonly onTogglePin: (identity: string) => void;
  /** Own identity (for chat authorship and the «you» marker). */
  readonly ownIdentity: string | null;
  /** Live roles and hands by identity, from the realtime channel. */
  readonly presence: ReadonlyMap<string, PanelPresence>;
  /** The local participant's role; gates the moderation surfaces. */
  readonly ownRole: ParticipantRole;
  /** Dispatches a moderate frame (mute/kick/stop_share; mute_all ignores the target). */
  readonly onModerate: (action: ModerationAction, target: string) => void;
  /** Dispatches a role frame (admin only). */
  readonly onSetRole: (target: string, role: AssignableRole) => void;
  /** Opens the invite dialog (the white «Пригласить» button of the people tab). */
  readonly onInvite: () => void;
  /**
   * Opens the invitation-links dialog (CR-20). Supplied for moderators/admins only; the
   * «Ссылки-приглашения» button renders beside the invite button only while it is set.
   */
  readonly onInvites?: (() => void) | undefined;
  /**
   * The recent moderation actions section (CR-22). Supplied for moderators/admins only; a
   * collapsible audit list at the bottom of the people tab. Undefined hides the section.
   */
  readonly recentActions?: RecentActionsProps | undefined;
  readonly chat: Omit<ChatPanelProps, 'ownIdentity'>;
  /**
   * Recordings tab content (M6). The page supplies it for moderators/admins only; the tab
   * renders when both this section and the local role permit it.
   */
  readonly recordings?: RecordingsPanelProps | undefined;
  /**
   * The lobby waiting list (CR-14). Staff-only data: the server holds lobby frames from
   * plain participants, so an empty list keeps the whole surface hidden - the section and the
   * tab badge render only while identities actually wait.
   */
  readonly lobbyPending?: readonly LobbyPendingItem[] | undefined;
  /** Staff lobby decision (CR-14): let this waiting identity into the room. */
  readonly onLobbyAdmit?: ((identity: string) => void) | undefined;
  /** Staff lobby decision (CR-14): refuse this waiting identity (close 4407 follows). */
  readonly onLobbyDeny?: ((identity: string) => void) | undefined;
  /**
   * Every member's LiveKit connection quality by identity (the unified room header): each
   * people row carries its bars. Undefined keeps the rows bar-less (tests, legacy callers).
   */
  readonly quality?: ReadonlyMap<string, string> | undefined;
}

/**
 * The right-hand panel in the Syncra call-screen look: a 320px aside on #101012 with
 * «Чат» / «Участники · N» tabs (plus «Записи» for moderators/admins) and the close button.
 * The participants tab carries the search box, the white invite button and the rows with
 * role badges, mic state, pin and the moderation kebab. On mobile the panel becomes a
 * full-screen overlay sheet sliding in from the right.
 */
export function SidePanel(props: SidePanelProps): JSX.Element {
  const { t } = useTranslation();
  const recordings = props.recordings;
  const showRecordings = recordings !== undefined && canModerate(props.ownRole);
  const lobbyPending = props.lobbyPending ?? [];
  return (
    <aside
      data-testid="side-panel"
      className={cn(
        'flex flex-col border-l border-white/[0.06]',
        props.isMobile
          ? 'fixed inset-y-0 right-0 z-40 w-full animate-in slide-in-from-right-10 bg-zinc-950/95 backdrop-blur duration-200'
          : // min-w-0: BR-DM-2 - the desktop column must shrink, never force the row wide.
            'w-[320px] min-w-0 shrink-0',
      )}
      style={{ background: props.isMobile ? undefined : '#101012' }}
    >
      <Tabs
        value={props.panel}
        onValueChange={(value) => {
          props.onPanelChange(value as PanelMode);
        }}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        {/* BR-DM-1/2: the tab row scrolls horizontally instead of pushing the header past the
            panel edge - the three tabs' min-content exceeds a 320px panel, which used to clip
            the close button off-screen and overflow the desktop room column by ~49px. */}
        <div className="flex shrink-0 items-center gap-1.5 border-b border-white/[0.06] px-3 pt-2.5">
          <div className="scroll-thin-dark min-w-0 flex-1 overflow-x-auto">
            <TabsList className="h-8 w-fit min-w-full justify-start gap-1 bg-white/[0.05] p-0.5">
              <TabsTrigger
                value="chat"
                className="h-7 gap-1.5 rounded-md px-3 text-[12.5px] text-zinc-400 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white"
              >
                <MessageSquare className="size-3.5" />
                {t('chat.tab')}
              </TabsTrigger>
              <TabsTrigger
                value="participants"
                className="h-7 gap-1.5 rounded-md px-3 text-[12.5px] text-zinc-400 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white"
              >
                <Users className="size-3.5" />
                {t('participants.tab')} · {props.tiles.length}
                {lobbyPending.length > 0 ? (
                  <span
                    data-testid="lobby-tab-badge"
                    title={t('lobby.panel_title')}
                    className="ml-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-amber-400/20 px-1 text-[10px] font-bold tabular-nums text-amber-300"
                  >
                    {lobbyPending.length}
                  </span>
                ) : null}
              </TabsTrigger>
              {showRecordings ? (
                <TabsTrigger
                  value="recordings"
                  data-testid="recordings-tab"
                  className="h-7 gap-1.5 rounded-md px-3 text-[12.5px] text-zinc-400 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white"
                >
                  <Disc className="size-3.5" />
                  {t('recording.tab')}
                </TabsTrigger>
              ) : null}
            </TabsList>
          </div>
          <button
            type="button"
            data-testid="close-panel"
            aria-label={t('participants.close_aria')}
            title={t('participants.close_aria')}
            onClick={props.onClose}
            className={cn(
              'grid shrink-0 place-items-center rounded-md transition-colors hover:bg-white/10 hover:text-white',
              // BR-DM-1: a 44px tap target on the mobile sheet - the close button is the only
              // way back once the full-screen panel covers the control pill.
              props.isMobile ? 'size-11 text-zinc-400' : 'size-7 text-zinc-500',
            )}
          >
            <X className={props.isMobile ? 'size-4' : 'size-3.5'} />
          </button>
        </div>
        <TabsContent value="chat" className="min-h-0 flex-1">
          <ChatPanel {...props.chat} ownIdentity={props.ownIdentity} />
        </TabsContent>
        <TabsContent value="participants" className="flex min-h-0 flex-1 flex-col">
          <ParticipantsList
            tiles={props.tiles}
            sharingIdentities={props.sharingIdentities}
            pinnedIdentity={props.pinnedIdentity}
            onTogglePin={props.onTogglePin}
            presence={props.presence}
            ownIdentity={props.ownIdentity}
            ownRole={props.ownRole}
            onModerate={props.onModerate}
            onSetRole={props.onSetRole}
            onInvite={props.onInvite}
            onInvites={props.onInvites}
            recentActions={props.recentActions}
            lobbyPending={lobbyPending}
            onLobbyAdmit={props.onLobbyAdmit}
            onLobbyDeny={props.onLobbyDeny}
            quality={props.quality}
          />
        </TabsContent>
        {showRecordings && recordings !== undefined ? (
          <TabsContent value="recordings" className="min-h-0 flex-1">
            <RecordingsPanel {...recordings} />
          </TabsContent>
        ) : null}
      </Tabs>
    </aside>
  );
}

interface ParticipantsListProps {
  readonly tiles: readonly ParticipantTile[];
  readonly sharingIdentities: ReadonlySet<string>;
  readonly pinnedIdentity: string | null;
  readonly onTogglePin: (identity: string) => void;
  readonly presence: ReadonlyMap<string, PanelPresence>;
  readonly ownIdentity: string | null;
  readonly ownRole: ParticipantRole;
  readonly onModerate: (action: ModerationAction, target: string) => void;
  readonly onSetRole: (target: string, role: AssignableRole) => void;
  readonly onInvite: () => void;
  /** Opens the invitation-links dialog (CR-20); staff-only via the page's prop gating. */
  readonly onInvites?: (() => void) | undefined;
  /** The recent moderation actions section (CR-22); undefined for non-staff. */
  readonly recentActions?: RecentActionsProps | undefined;
  /** Lobby waiting list (CR-14); empty or absent keeps the section hidden. */
  readonly lobbyPending: readonly LobbyPendingItem[];
  readonly onLobbyAdmit?: ((identity: string) => void) | undefined;
  readonly onLobbyDeny?: ((identity: string) => void) | undefined;
  /** Every member's LiveKit connection quality by identity (the unified room header). */
  readonly quality?: ReadonlyMap<string, string> | undefined;
}

/** Case-insensitive match of the query against display name and identity. */
function matchesQuery(tile: ParticipantTile, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return true;
  }
  return tile.label.toLowerCase().includes(needle) || tile.identity.toLowerCase().includes(needle);
}

/** The people tab: search box plus the white invite button on top, then the rows. */
function ParticipantsList({
  tiles,
  sharingIdentities,
  pinnedIdentity,
  onTogglePin,
  presence,
  ownIdentity,
  ownRole,
  onModerate,
  onSetRole,
  onInvite,
  onInvites,
  recentActions,
  lobbyPending,
  onLobbyAdmit,
  onLobbyDeny,
  quality,
}: ParticipantsListProps): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const visible = useMemo(() => tiles.filter((tile) => matchesQuery(tile, query)), [tiles, query]);
  const showLobby =
    lobbyPending.length > 0 && onLobbyAdmit !== undefined && onLobbyDeny !== undefined;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Lobby waiting list (CR-14): staff see who is held at the gate and decide per person.
          The section renders only while identities actually wait - hidden entirely otherwise. */}
      {showLobby ? (
        <div
          data-testid="lobby-panel"
          aria-label={t('lobby.panel_title')}
          className="shrink-0 border-b border-amber-500/20 bg-amber-500/[0.04] p-3"
        >
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-300">
            <Hourglass aria-hidden className="size-3.5" />
            {t('lobby.panel_title', { count: lobbyPending.length })}
          </p>
          <ul className="mt-2 flex flex-col gap-1.5" data-testid="lobby-list">
            {lobbyPending.map((item) => (
              <li
                key={item.identity}
                data-testid={`lobby-item-${item.identity}`}
                className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.03] px-2 py-1.5"
              >
                <UserAvatar name={item.displayName} size="md" className="size-7 text-xs" />
                <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-zinc-200">
                  {item.displayName}
                </span>
                <button
                  type="button"
                  data-testid={`lobby-admit-${item.identity}`}
                  aria-label={t('lobby.admit_aria', { name: item.displayName })}
                  title={t('lobby.admit')}
                  onClick={() => {
                    onLobbyAdmit?.(item.identity);
                  }}
                  className="grid size-7 shrink-0 place-items-center rounded-md bg-emerald-500/15 text-emerald-300 transition-colors hover:bg-emerald-500/25"
                >
                  <Check className="size-3.5" />
                </button>
                <button
                  type="button"
                  data-testid={`lobby-deny-${item.identity}`}
                  aria-label={t('lobby.deny_aria', { name: item.displayName })}
                  title={t('lobby.deny')}
                  onClick={() => {
                    onLobbyDeny?.(item.identity);
                  }}
                  className="grid size-7 shrink-0 place-items-center rounded-md bg-red-500/15 text-red-300 transition-colors hover:bg-red-500/25"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex shrink-0 flex-col gap-2 border-b border-white/[0.06] p-3">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-zinc-600"
          />
          <Input
            data-testid="participants-search"
            aria-label={t('participants.search_aria')}
            placeholder={t('participants.search_placeholder')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            className="h-8 rounded-lg border-white/10 bg-white/[0.05] pl-8 text-[12.5px] text-white placeholder:text-zinc-600 focus-visible:border-white/20"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="invite-people"
            onClick={onInvite}
            className="flex h-8 w-full flex-1 items-center justify-center gap-1.5 rounded-lg bg-white text-[12.5px] font-semibold text-zinc-950 transition-colors hover:bg-zinc-200"
          >
            <UserPlus className="size-3.5" />
            {t('participants.invite_button')}
          </button>
          {onInvites !== undefined ? (
            <button
              type="button"
              data-testid="invites-open"
              title={t('invites.open_title')}
              onClick={onInvites}
              className="flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2 text-[11px] font-medium text-emerald-200 transition-colors hover:bg-emerald-500/20"
            >
              <Link2 className="size-3.5" />
              {t('invites.open')}
            </button>
          ) : null}
          {canMuteAll(ownRole) ? (
            <button
              type="button"
              data-testid="mute-all"
              title={t('participants.mute_all_title')}
              onClick={() => {
                onModerate('mute_all', '');
              }}
              className="flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 text-[11px] font-medium text-amber-200 transition-colors hover:bg-amber-500/20"
            >
              <MicOff className="size-3.5" />
              {t('participants.mute_all')}
            </button>
          ) : null}
        </div>
      </div>
      {visible.length === 0 ? (
        <p className="px-2 py-4 text-center text-xs text-zinc-500">
          {t('participants.empty_search')}
        </p>
      ) : (
        <ul
          className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2 scroll-thin-dark"
          aria-label={t('room.participants_aria')}
        >
          {' '}
          {visible.map((tile) => {
            const pinned = pinnedIdentity === tile.identity;
            const live = presence.get(tile.identity);
            const role = live?.role ?? 'participant';
            const handRaised = live?.handRaised ?? false;
            const sharing = sharingIdentities.has(tile.identity);
            const menuActions = canModerate(ownRole)
              ? moderationActionsFor({
                  ownRole,
                  targetRole: role,
                  isSelf: tile.identity === ownIdentity,
                  isSharing: sharing,
                })
              : [];
            return (
              <li
                key={tile.identity}
                data-testid={`participant-row-${tile.identity}`}
                className="group flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-white/[0.04]"
              >
                <UserAvatar name={tile.label} size="md" ring={sharing} className="size-8 text-xs" />
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[12.5px] font-medium text-zinc-200">
                      {tile.isLocal ? t('participants.you_display') : tile.label}
                    </span>
                    {handRaised ? <HandBadge identity={tile.identity} /> : null}
                  </div>
                  <div className="truncate text-[10.5px] text-zinc-500">
                    {sharing
                      ? t('participants.status_sharing')
                      : tile.videoTrack === null
                        ? t('participants.status_camera_off')
                        : t('participants.status_in_room')}
                  </div>
                </div>
                {role !== 'participant' ? (
                  <RoleBadgeInline identity={tile.identity} role={role} />
                ) : null}
                {sharing ? <ScreenShare className="size-3.5 shrink-0 text-emerald-400" /> : null}
                <span
                  data-testid={`panel-mic-${tile.identity}`}
                  aria-label={
                    tile.micMuted ? t('participants.mic_muted') : t('participants.mic_on')
                  }
                  title={tile.micMuted ? t('participants.mic_muted') : t('participants.mic_on')}
                  className="shrink-0"
                >
                  {tile.micMuted ? (
                    <MicOff className="size-3.5 text-red-400" />
                  ) : (
                    <Mic className="size-3.5 text-zinc-500" />
                  )}
                </span>
                {quality !== undefined ? (
                  <span
                    data-testid={`panel-quality-${tile.identity}`}
                    title={t('participants.quality_title')}
                    className="shrink-0"
                  >
                    <SignalBars level={signalLevel(quality.get(tile.identity) ?? 'unknown')} />
                  </span>
                ) : null}
                <button
                  type="button"
                  data-testid={`panel-pin-${tile.identity}`}
                  aria-label={pinned ? t('participants.unpin') : t('participants.pin')}
                  title={pinned ? t('participants.unpin') : t('participants.pin')}
                  onClick={() => {
                    onTogglePin(tile.identity);
                  }}
                  className={cn(
                    'grid size-7 shrink-0 place-items-center rounded-md text-zinc-400 transition-colors hover:bg-white/10 hover:text-white',
                    pinned
                      ? 'text-emerald-300'
                      : 'opacity-0 group-hover:opacity-100 focus:opacity-100',
                  )}
                >
                  {pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
                </button>
                <ModerationMenu
                  identity={tile.identity}
                  displayName={tile.label}
                  actions={menuActions}
                  onAction={(action, target) => {
                    if (action === 'promote') {
                      onSetRole(target, 'moderator');
                      return;
                    }
                    if (action === 'demote') {
                      onSetRole(target, 'participant');
                      return;
                    }
                    onModerate(action, target);
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
      {/* The staff's recent moderation actions (CR-22): a collapsible audit list pinned to the
          bottom of the people tab; the page supplies it for moderators/admins only. */}
      {recentActions !== undefined ? (
        <RecentActions
          room={recentActions.room}
          getToken={recentActions.getToken}
          refreshKey={recentActions.refreshKey}
        />
      ) : null}
    </div>
  );
}

/**
 * Role badge of a people-tab row in the Syncra language: amber «Хост» for admins, zinc
 * «Модератор» for moderators. Kept beside the row (not inline in the name) so long names
 * never push it out of view.
 */
function RoleBadgeInline({
  identity,
  role,
}: {
  readonly identity: string;
  readonly role: 'admin' | 'moderator';
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`role-badge-${identity}`}
      title={
        role === 'admin'
          ? t('participants.role_admin_title')
          : t('participants.role_moderator_title')
      }
      className={cn(
        'shrink-0 rounded px-1 py-px text-[9.5px] font-bold tracking-wide uppercase',
        role === 'admin' ? 'bg-amber-400/15 text-amber-400' : 'bg-sky-500/15 text-sky-300',
      )}
    >
      {t(role === 'admin' ? 'participants.badge_host' : 'participants.badge_moderator')}
    </span>
  );
}
